/** An edit TinyMCE painted on the item itself that a conversion leaves behind. */
export type EditorTextBoxConversionDroppedEdit =
  | 'background'
  | 'opacity'
  | 'border'
  | 'drop-shadow'

/**
 * One thing a conversion did not carry, or carried differently. `key` on a
 * frozen field is the key the TinyMCE markup stored (`organizations[0].name`,
 * `children`). Where the field showed, its stored text is kept as the text it
 * had at conversion; where TinyMCE hid it, it converts to nothing and is
 * still reported.
 */
export type EditorTextBoxConversionReportEntry =
  | { kind: 'frozen-field'; key: string; count: number }
  | { kind: 'table-frozen'; count: number }
  | { kind: 'service-binding-dropped'; count: number }
  | { kind: 'additional-family-more-detail' }
  | { kind: 'name-styled-as-one-run' }
  | { kind: 'placeholder-only' }
  | { kind: 'shadow-skipped'; color: string }
  | { kind: 'edit-dropped'; edit: EditorTextBoxConversionDroppedEdit }
  | { kind: 'highlight-dropped'; count: number }
  | { kind: 'family-outside-picker'; family: string }

/** Every entry a conversion made, most consequential first; empty when nothing was lost. */
export type EditorTextBoxConversionReport =
  readonly EditorTextBoxConversionReportEntry[]

export type EditorTextBoxConversionErrorReason =
  | 'item-id'
  | 'geometry'
  | 'height'
  | 'width'
  | 'run-length'
  | 'run-count'
  | 'block-count'
  | 'field'

/**
 * A TinyMCE item the converter cannot turn into a box the row store would
 * accept. It is raised before anything is returned, so nothing is written.
 */
export class EditorTextBoxConversionError extends Error {
  readonly reason: EditorTextBoxConversionErrorReason

  constructor(reason: EditorTextBoxConversionErrorReason, message: string) {
    super(message)
    this.name = 'EditorTextBoxConversionError'
    this.reason = reason
  }
}

type EntryKind = EditorTextBoxConversionReportEntry['kind']

/** The order entries are reported in: content that stopped being live first. */
const KIND_ORDER: readonly EntryKind[] = [
  'frozen-field',
  'table-frozen',
  'service-binding-dropped',
  'additional-family-more-detail',
  'name-styled-as-one-run',
  'placeholder-only',
  'shadow-skipped',
  'edit-dropped',
  'highlight-dropped',
  'family-outside-picker',
]

/** What makes two entries the same entry: their kind and what they name. */
const identityOf = (entry: EditorTextBoxConversionReportEntry): string => {
  switch (entry.kind) {
    case 'frozen-field':
      return `${entry.kind}:${entry.key}`
    case 'shadow-skipped':
      return `${entry.kind}:${entry.color}`
    case 'edit-dropped':
      return `${entry.kind}:${entry.edit}`
    case 'family-outside-picker':
      return `${entry.kind}:${entry.family}`
    default:
      return entry.kind
  }
}

export type EditorTextBoxConversionReportCollector = {
  /** Records an entry; a repeat of a counted entry adds its count to the first. */
  add: (entry: EditorTextBoxConversionReportEntry) => void
  entries: () => EditorTextBoxConversionReport
}

export const editorTextBoxConversionReportCollector =
  (): EditorTextBoxConversionReportCollector => {
    const byIdentity = new Map<string, EditorTextBoxConversionReportEntry>()
    return {
      add: (entry) => {
        const identity = identityOf(entry)
        const prior = byIdentity.get(identity)
        if (prior === undefined) {
          byIdentity.set(identity, { ...entry })
          return
        }
        if ('count' in prior && 'count' in entry)
          byIdentity.set(identity, {
            ...prior,
            count: prior.count + entry.count,
          })
      },
      entries: () =>
        [...byIdentity.values()].sort(
          (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind),
        ),
    }
  }
